import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

interface CollectionActivity {
  collectionId: string;
  unreadMessages: number;
  newItemsCount: number;
}

/**
 * Fetches unread message counts and new item counts for multiple collections.
 * "New" items are those added after the user's last visit to the collection.
 */
export function useCollectionsUnreadActivity(collectionIds: string[]) {
  const { user } = useAuth();

  const { data: activityMap = {}, isLoading } = useQuery({
    queryKey: ['collections-unread-activity', collectionIds, user?.id],
    queryFn: async () => {
      if (!user || collectionIds.length === 0) return {};

      // Fetch last read timestamps for messages
      const { data: messageReads } = await supabase
        .from('collection_message_reads')
        .select('collection_id, last_read_at')
        .eq('user_id', user.id)
        .in('collection_id', collectionIds);

      // Create a map of collection_id -> last_read_at
      const lastReadMap: Record<string, string | null> = {};
      messageReads?.forEach((read) => {
        lastReadMap[read.collection_id] = read.last_read_at;
      });

      // For each collection, count unread messages
      const activityResults: Record<string, CollectionActivity> = {};

      // Batch query: get all messages for these collections
      const { data: allMessages } = await supabase
        .from('collection_messages')
        .select('id, collection_id, created_at, user_id')
        .in('collection_id', collectionIds)
        .neq('user_id', user.id);

      // Count unread messages per collection
      collectionIds.forEach((collectionId) => {
        const lastRead = lastReadMap[collectionId];
        const messagesForCollection = allMessages?.filter(
          (m) => m.collection_id === collectionId
        ) || [];

        const unreadMessages = lastRead
          ? messagesForCollection.filter((m) => m.created_at > lastRead).length
          : messagesForCollection.length;

        activityResults[collectionId] = {
          collectionId,
          unreadMessages,
          newItemsCount: 0, // Will be calculated below
        };
      });

      // Get last visited timestamps for collections (we can use message_reads as a proxy)
      // For new items, check items added after last_read_at or in the last 48 hours
      const { data: recentItems } = await supabase
        .from('collection_items')
        .select('collection_id, added_at')
        .in('collection_id', collectionIds)
        .gte('added_at', new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString());

      // Count new items per collection (items added after last read, by someone else)
      recentItems?.forEach((item) => {
        const lastRead = lastReadMap[item.collection_id];
        // If user has read this collection before, only count items after that
        // Otherwise count all recent items
        if (!lastRead || item.added_at > lastRead) {
          if (activityResults[item.collection_id]) {
            activityResults[item.collection_id].newItemsCount++;
          }
        }
      });

      return activityResults;
    },
    enabled: !!user && collectionIds.length > 0,
    staleTime: 30000, // 30 seconds
  });

  return {
    activityMap,
    isLoading,
    getActivity: (collectionId: string): CollectionActivity | undefined => 
      activityMap[collectionId],
  };
}
